import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  boardCollapsedListsSchema,
  boardPinnedViewsSchema,
  boardViewKindSchema,
  boardViewPreferenceSchema,
  displayPreferencesSchema,
  focusModePatchSchema,
  type BoardContents,
  type BoardCollapsedLists,
  type BoardPinnedViews,
  type DisplayPreferences,
  type ListCollapseState,
  type BoardViewKind,
  type BoardViewPreference,
  listCollapseStateSchema,
} from '@molt/shared';

import { del, get, patch, put } from './api';
import type { LocaleFormattingTranslate } from './locale-formatting';
import { queryKeys } from './query-keys';

/**
 * FE-80.1 — `label` is GONE from this record, and the deletion is the point.
 *
 * It held six English nouns (`Board`, `Table`, `Calendar`, …) that the
 * catalogue ALREADY declares as `boardFace.view.*`, which made this module a
 * second author of words `BoardFace.tsx` was routing correctly beside it. The
 * board-face screenshot pass photographed the consequence: a board face
 * otherwise entirely Spanish whose view switcher read `Board` and whose menu
 * read `Calendar`.
 *
 * ⚠ The field is DELETED rather than left unread. A bypassed constant is still
 * an author — it keeps compiling, keeps reading correct, and is the obvious
 * thing for a later slice to reach for. `board-view.routing.test.ts` pins the
 * absence.
 */
export interface BoardViewOption {
  readonly kind: BoardViewKind;
  readonly available: boolean;
}

export interface BoardViewCardSummary {
  readonly id: string;
  readonly title: string;
  readonly path: string;
  readonly listTitle: string;
  readonly dueAt: string | null;
  /** BE-44.7 (FR-EXP-029): carried beside `dueAt` so calendar/timeline shells can render the date-only form. */
  readonly dueAllDay: boolean;
  readonly startAt: string | null;
}

/**
 * FE-80.1 — a DISCRIMINANT, where this was an English fragment.
 *
 * ⚠ `reason` used to be the string `'without due dates'`, and its two consumers
 * spliced it into DIFFERENT frames: `BoardViewSwitcher` built
 * `` `${count} ${cards} ${reason}` `` and `BoardViewShell` built
 * `` `These cards are ${reason}.` ``. One routed fragment cannot serve both —
 * `lib/dnd.ts` states the rule as six keys for six sentences, not one sentence
 * plus conditional fragments — so the fragment is replaced by a token and each
 * call site owns a whole plural-keyed sentence of its own.
 *
 * ⚠ And that is why `boardViewAccounting` still takes no `t`. Threading a
 * translator through it would drag in `BoardTimelineView`, which reads only
 * `unrenderableCards` and renders none of these words.
 */
export type BoardViewExclusionReason = 'noDueDate' | 'noDates';

export interface BoardViewAccounting {
  readonly totalCards: number;
  readonly unrenderableCards: readonly BoardViewCardSummary[];
  readonly reason: BoardViewExclusionReason | null;
}

export interface BoardLocalViewChromeState {
  readonly pinnedViews: readonly BoardViewKind[];
  readonly focusMode: boolean;
}

export const BOARD_VIEW_OPTIONS: readonly BoardViewOption[] = [
  { kind: 'board', available: true },
  { kind: 'table', available: true },
  { kind: 'calendar', available: true },
  { kind: 'timeline', available: true },
  { kind: 'dashboard', available: true },
  { kind: 'map', available: true },
];

export const MOBILE_BOARD_VIEW_KINDS = ['board', 'calendar'] as const;

export function isBoardViewAvailableOnMobile(kind: BoardViewKind): boolean {
  return MOBILE_BOARD_VIEW_KINDS.includes(
    kind as (typeof MOBILE_BOARD_VIEW_KINDS)[number],
  );
}

export function boardViewForViewport(
  kind: BoardViewKind,
  mobile: boolean,
): BoardViewKind {
  return mobile && !isBoardViewAvailableOnMobile(kind) ? 'board' : kind;
}

/**
 * ⚠ Moved here from `BoardFace.tsx`, where it was a private helper, so that the
 * interpolated key family has ONE builder. `BoardFace` and this module were
 * each turning a `BoardViewKind` into a display word; only one of them was
 * asking the catalogue.
 *
 * Exported because `board-view.routing.test.ts`'s enumeration assert names it:
 * the `t('…')` anchor every routing pin in this repository uses is
 * structurally blind to a key built by interpolation, so a seventh board view
 * would reach production undeclared and render `boardFace.view.gantt` at a
 * reader.
 */
export function boardViewMessageKey(kind: BoardViewKind): string {
  return `boardFace.view.${kind}`;
}

/**
 * ⚠ `t` FIRST and REQUIRED, matching `cardDisplayTitle` and `dragInstructions`.
 * A defaulted translator is the shape that lets one caller fall through to a
 * default that looks correct; a required one makes `tsc` enumerate the callers,
 * which is what pulled `BoardViewShell` into this slice rather than leaving it
 * as a half-translated sibling.
 */
export function boardViewLabel(
  t: LocaleFormattingTranslate,
  kind: BoardViewKind,
): string {
  return t(boardViewMessageKey(kind));
}

/**
 * ── The local record is a FIRST-FRAME HINT, for BOTH of its fields ──────────
 *
 * `FE-55.1` gave `pinnedViews` the same treatment `FE-55.3` gave `focusMode`:
 * a durable half on the server (`GET`/`PUT /boards/:id/pinned-views`, below)
 * that OUTRANKS this record. Neither function is deleted and neither field is
 * dropped — `BoardLocalViewChromeState` still round-trips both, so a rollback
 * to the previous build loses nothing and `focusMode` keeps working through
 * exactly the calls it always did.
 *
 * ⚠ What changed is AUTHORITY, not shape. The record's only remaining job is
 * to paint the first frame: a bare query replaces a synchronous read with an
 * async one, so the pinned strip would vanish for a frame on EVERY board open.
 * That is a board that reads worse at a glance, and the product tie-breaker
 * says the feature loses. Read local first, let the server correct it — and on
 * a disagreement the SERVER wins, except for the one bounded seed case
 * `BoardFace` owns (an empty server set beside a non-empty local hint, which
 * would otherwise unpin every member in the product on the day this lands).
 */
export function localBoardViewChromeKey(userId: string, boardId: string): string {
  return `molt:board-view-chrome:${userId}:${boardId}`;
}

/**
 * ⚠ `FE-04.18`, recorded rather than repaired. This `Map` is the fallback for a
 * `window`-less render, and the guard below reaches it whenever
 * `window.localStorage` exposes no `getItem`. Under Vitest that was ALWAYS —
 * the global exists with no methods at all — so from `E3` until `FE-04.18`
 * every unit test of the two helpers below ran on this `Map` and none of them
 * could tell. `view-state.test.ts` now reads `window.localStorage` directly to
 * pin which branch ran.
 *
 * ⚠ It is MODULE SCOPE, and `vi.unstubAllGlobals()` does not clear it: a test
 * that runs without a Storage writes here, and a later test in the same file
 * that also runs without one reads it back. Nothing trips that today. It is
 * left alone because the fallback is shipped behaviour, and a harness item has
 * no business rewriting production code to suit its own tests.
 */
const localBoardViewChromeFallback = new Map<string, string>();

function readLocalBoardViewChromeValue(key: string): string | null {
  const storage = typeof window === 'undefined' ? undefined : window.localStorage;
  if (typeof storage?.getItem === 'function') return storage.getItem(key);
  return localBoardViewChromeFallback.get(key) ?? null;
}

function writeLocalBoardViewChromeValue(key: string, value: string): void {
  const storage = typeof window === 'undefined' ? undefined : window.localStorage;
  if (typeof storage?.setItem === 'function') {
    storage.setItem(key, value);
    return;
  }
  localBoardViewChromeFallback.set(key, value);
}

export function loadLocalBoardViewChrome(
  userId: string | undefined,
  boardId: string,
): BoardLocalViewChromeState {
  if (userId === undefined || typeof window === 'undefined') {
    return { pinnedViews: [], focusMode: false };
  }

  const raw = readLocalBoardViewChromeValue(localBoardViewChromeKey(userId, boardId));
  if (raw === null) return { pinnedViews: [], focusMode: false };

  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object') {
      return { pinnedViews: [], focusMode: false };
    }
    const record = parsed as Partial<BoardLocalViewChromeState>;
    return {
      pinnedViews: normalisePinnedViews(record.pinnedViews ?? []),
      focusMode: record.focusMode === true,
    };
  } catch {
    return { pinnedViews: [], focusMode: false };
  }
}

export function saveLocalBoardViewChrome(
  userId: string | undefined,
  boardId: string,
  state: BoardLocalViewChromeState,
): void {
  if (userId === undefined || typeof window === 'undefined') return;
  writeLocalBoardViewChromeValue(
    localBoardViewChromeKey(userId, boardId),
    JSON.stringify({
      pinnedViews: normalisePinnedViews(state.pinnedViews),
      focusMode: state.focusMode,
    }),
  );
}

export function normalisePinnedViews(views: readonly unknown[]): BoardViewKind[] {
  const seen = new Set<BoardViewKind>();
  const pinned: BoardViewKind[] = [];
  for (const view of views) {
    const parsed = listViewKind(view);
    if (parsed === null || seen.has(parsed)) continue;
    seen.add(parsed);
    pinned.push(parsed);
  }
  return pinned;
}

export function togglePinnedView(
  views: readonly BoardViewKind[],
  view: BoardViewKind,
): BoardViewKind[] {
  return views.includes(view)
    ? views.filter((candidate) => candidate !== view)
    : normalisePinnedViews([...views, view]);
}

export function movePinnedView(
  views: readonly BoardViewKind[],
  view: BoardViewKind,
  direction: 'left' | 'right',
): BoardViewKind[] {
  const next = normalisePinnedViews(views);
  const index = next.indexOf(view);
  if (index === -1) return next;
  const target = direction === 'left' ? index - 1 : index + 1;
  if (target < 0 || target >= next.length) return next;
  [next[index], next[target]] = [next[target]!, next[index]!];
  return next;
}

export function dragPinnedView(
  views: readonly BoardViewKind[],
  view: BoardViewKind,
  targetView: BoardViewKind,
  position: 'before' | 'after',
): BoardViewKind[] {
  const next = normalisePinnedViews(views);
  const from = next.indexOf(view);
  const target = next.indexOf(targetView);
  if (from === -1 || target === -1 || view === targetView) return next;

  const [moved] = next.splice(from, 1);
  const targetAfterRemoval = next.indexOf(targetView);
  const insertion = position === 'before' ? targetAfterRemoval : targetAfterRemoval + 1;
  next.splice(insertion, 0, moved!);
  return next;
}

function listViewKind(value: unknown): BoardViewKind | null {
  const parsed = boardViewKindSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function getBoardViewPreference(boardId: string): Promise<BoardViewPreference> {
  return get(`/boards/${encodeURIComponent(boardId)}/view`, boardViewPreferenceSchema);
}

export async function setBoardViewPreference(
  boardId: string,
  lastView: BoardViewKind,
): Promise<BoardViewPreference> {
  return put(
    `/boards/${encodeURIComponent(boardId)}/view`,
    boardViewPreferenceSchema,
    boardViewPreferenceSchema.parse({ lastView }),
  );
}

export function boardViewPreferenceQueryOptions(boardId: string) {
  return queryOptions({
    queryKey: queryKeys.boards.view(boardId),
    queryFn: () => getBoardViewPreference(boardId),
  });
}

export function getBoardCollapsedLists(boardId: string): Promise<BoardCollapsedLists> {
  return get(
    `/boards/${encodeURIComponent(boardId)}/collapsed-lists`,
    boardCollapsedListsSchema,
  );
}

export function collapseList(listId: string): Promise<ListCollapseState> {
  return put(`/lists/${encodeURIComponent(listId)}/collapse`, listCollapseStateSchema);
}

export function expandList(listId: string): Promise<ListCollapseState> {
  return del(`/lists/${encodeURIComponent(listId)}/collapse`, listCollapseStateSchema);
}

export function boardCollapsedListsQueryOptions(boardId: string) {
  return queryOptions({
    queryKey: queryKeys.boards.collapsedLists(boardId),
    queryFn: () => getBoardCollapsedLists(boardId),
  });
}

export function collapsedListIdSet(
  state: BoardCollapsedLists | undefined,
): ReadonlySet<string> {
  return new Set(state?.listIds ?? []);
}

interface BoardViewPreferenceMutationContext {
  readonly previous: BoardViewPreference | undefined;
  readonly optimistic: BoardViewPreference;
}

export function useSetBoardViewPreference(
  boardId: string,
): UseMutationResult<BoardViewPreference, Error, BoardViewKind> {
  const queryClient = useQueryClient();
  const key = queryKeys.boards.view(boardId);

  return useMutation<
    BoardViewPreference,
    Error,
    BoardViewKind,
    BoardViewPreferenceMutationContext
  >({
    mutationFn: (lastView) => setBoardViewPreference(boardId, lastView),
    onMutate: async (lastView) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<BoardViewPreference>(key);
      const optimistic = { lastView };
      queryClient.setQueryData<BoardViewPreference>(key, optimistic);
      return { previous, optimistic };
    },
    onSuccess: (preference, _lastView, context) => {
      const current = queryClient.getQueryData<BoardViewPreference>(key);
      if (current?.lastView !== context.optimistic.lastView) return;
      queryClient.setQueryData<BoardViewPreference>(key, preference);
    },
    onError: (_error, _lastView, context) => {
      if (context === undefined) return;
      const current = queryClient.getQueryData<BoardViewPreference>(key);
      if (current?.lastView !== context.optimistic.lastView) return;
      queryClient.setQueryData<BoardViewPreference>(key, context.previous);
    },
  });
}

export interface SetListCollapseVariables {
  readonly boardId: string;
  readonly listId: string;
  readonly collapsed: boolean;
}

interface ListCollapseMutationContext {
  readonly key: ReturnType<typeof queryKeys.boards.collapsedLists>;
  readonly previous: BoardCollapsedLists | undefined;
  readonly variables: SetListCollapseVariables;
}

export function useSetListCollapse(): UseMutationResult<
  ListCollapseState,
  Error,
  SetListCollapseVariables
> {
  const queryClient = useQueryClient();

  return useMutation<
    ListCollapseState,
    Error,
    SetListCollapseVariables,
    ListCollapseMutationContext
  >({
    mutationFn: ({ listId, collapsed }) =>
      collapsed ? collapseList(listId) : expandList(listId),
    onMutate: async (variables) => {
      const key = queryKeys.boards.collapsedLists(variables.boardId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<BoardCollapsedLists>(key);
      queryClient.setQueryData<BoardCollapsedLists>(
        key,
        withCollapseState(previous, variables),
      );
      return { key, previous, variables };
    },
    onSuccess: (state, _variables, context) => {
      if (context === undefined) return;
      const current = queryClient.getQueryData<BoardCollapsedLists>(context.key);
      if (!matchesCollapseState(current, context.variables)) return;
      queryClient.setQueryData<BoardCollapsedLists>(
        context.key,
        withCollapseState(current, {
          ...context.variables,
          collapsed: state.collapsed,
        }),
      );
    },
    onError: (_error, _variables, context) => {
      if (context === undefined) return;
      const current = queryClient.getQueryData<BoardCollapsedLists>(context.key);
      if (!matchesCollapseState(current, context.variables)) return;
      queryClient.setQueryData<BoardCollapsedLists>(
        context.key,
        context.previous ?? { listIds: [] },
      );
    },
  });
}

function withCollapseState(
  state: BoardCollapsedLists | undefined,
  variables: SetListCollapseVariables,
): BoardCollapsedLists {
  const ids = new Set(state?.listIds ?? []);
  if (variables.collapsed) {
    ids.add(variables.listId);
  } else {
    ids.delete(variables.listId);
  }
  return { listIds: [...ids] };
}

function matchesCollapseState(
  state: BoardCollapsedLists | undefined,
  variables: SetListCollapseVariables,
): boolean {
  return (state?.listIds.includes(variables.listId) ?? false) === variables.collapsed;
}

export function boardViewAccounting(
  view: BoardViewKind,
  contents: BoardContents,
): BoardViewAccounting {
  const cards = contents.lists.flatMap((list) =>
    list.cards.map((card) => ({
      card,
      listTitle: list.title,
    })),
  );

  const unrenderableCards = cards
    .filter(({ card }) => {
      if (view === 'calendar') return card.dueAt === null;
      if (view === 'timeline') return card.startAt === null && card.dueAt === null;
      return false;
    })
    .map(({ card, listTitle }) => ({
      id: card.id,
      title: card.title,
      path: card.path,
      listTitle,
      dueAt: card.dueAt,
      dueAllDay: card.dueAllDay,
      startAt: card.startAt,
    }));

  return {
    totalCards: cards.length,
    unrenderableCards,
    reason: accountingReason(view, unrenderableCards.length),
  };
}

function accountingReason(
  view: BoardViewKind,
  count: number,
): BoardViewExclusionReason | null {
  if (count === 0) return null;
  if (view === 'calendar') return 'noDueDate';
  if (view === 'timeline') return 'noDates';
  return null;
}

/**
 * ── Focus mode's durable half — `FR-VIEW-011`, closing `FE-55.3`'s residual ─
 *
 * Until this chunk, focus mode lived only in `loadLocalBoardViewChrome` above:
 * one browser, one machine, and gone the moment a member signed in elsewhere.
 * `GET`/`PATCH /me/display-preferences` make it durable.
 *
 * ⚠ **The two are scoped differently and the server wins.** The local key is
 * `molt:board-view-chrome:<userId>:<boardId>` — user AND board. The column is
 * `user_preferences.focus_mode_enabled`, keyed by `user_id` alone with no
 * `board_id`, and this chunk wrote no migration. `FR-VIEW-011` AC2 asks that
 * focus mode survive a reload *"for that member"*, not for that board, so the
 * requirement is indifferent and the catalogue decides.
 *
 * **A member who turns focus mode on for one board and opens another finds it
 * on there too.** The local record is not deleted — `saveLocalBoardViewChrome`
 * still round-trips `focusMode`, so a rollback to the previous build loses
 * nothing — but it stops being an authority. Its only remaining job is to
 * paint the first frame before the query resolves, so a member who left focus
 * mode on does not watch the chrome appear and then vanish.
 */

export function getDisplayPreferences(): Promise<DisplayPreferences> {
  return get('/me/display-preferences', displayPreferencesSchema);
}

/**
 * ⚠ `async`, matching `setBoardViewPreference` above, so that
 * `focusModePatchSchema.parse` failing produces a REJECTED PROMISE rather than
 * a synchronous throw. A caller that awaits it sees the same shape either way;
 * a caller that hands the promise to TanStack Query does not, and a
 * synchronous throw from a `mutationFn` escapes the mutation's own error
 * handling.
 */
export async function setFocusMode(
  focusModeEnabled: boolean,
): Promise<DisplayPreferences> {
  return patch(
    '/me/display-preferences/focus-mode',
    displayPreferencesSchema,
    focusModePatchSchema.parse({ focusModeEnabled }),
  );
}

/**
 * `enabled` gates the query on a real session rather than letting it 401 on
 * every anonymous board view. An anonymous reader has no member preferences
 * and cannot enter focus mode, so there is nothing to ask for.
 */
export function displayPreferencesQueryOptions(signedIn: boolean) {
  return queryOptions({
    queryKey: queryKeys.meDisplayPreferences(),
    queryFn: getDisplayPreferences,
    enabled: signedIn,
  });
}

interface FocusModeMutationContext {
  readonly previous: DisplayPreferences | undefined;
  readonly optimistic: DisplayPreferences;
}

/**
 * Optimistic, in the same shape as `useSetBoardViewPreference` above, and for
 * the same reason: focus mode is a control a member toggles while presenting,
 * so the chrome must go the instant they click rather than one round trip
 * later.
 *
 * ⚠ Both `onSuccess` and `onError` re-check that the cache still holds *this*
 * mutation's optimistic value before writing. Two rapid toggles otherwise let
 * the slower response land last and win, which reads as the button not
 * working.
 */
export function useSetFocusMode(): UseMutationResult<DisplayPreferences, Error, boolean> {
  const queryClient = useQueryClient();
  const key = queryKeys.meDisplayPreferences();

  return useMutation<DisplayPreferences, Error, boolean, FocusModeMutationContext>({
    mutationFn: setFocusMode,
    onMutate: async (focusModeEnabled) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<DisplayPreferences>(key);
      const optimistic = { focusModeEnabled };
      queryClient.setQueryData<DisplayPreferences>(key, optimistic);
      return { previous, optimistic };
    },
    onSuccess: (preferences, _enabled, context) => {
      const current = queryClient.getQueryData<DisplayPreferences>(key);
      if (current?.focusModeEnabled !== context.optimistic.focusModeEnabled) return;
      queryClient.setQueryData<DisplayPreferences>(key, preferences);
    },
    onError: (_error, _enabled, context) => {
      if (context === undefined) return;
      const current = queryClient.getQueryData<DisplayPreferences>(key);
      if (current?.focusModeEnabled !== context.optimistic.focusModeEnabled) return;
      queryClient.setQueryData<DisplayPreferences>(key, context.previous);
    },
  });
}

/**
 * ── Pinned views' durable half — `FR-VIEW-004`, closing `FE-55.1`'s residual ─
 *
 * `BE-55.3` shipped `GET`/`PUT /boards/:id/pinned-views` and
 * `packages/shared/src/board-pinned-view.ts` on 2026-09-01, and nothing in
 * `apps/web/src` called either of them: a scan of this tree for
 * `/pinned-views` returned zero hits, and the pinned order lived only in the
 * `localStorage` record above. `FR-VIEW-004` AC2 says the reordered strip
 * "persists for that member" — not "persists on that device" — so the
 * requirement had always asked for the durable answer.
 *
 * ⚠ The two are scoped THE SAME WAY, which is the difference from focus mode.
 * `board_pinned_views`'s primary key is `(user_id, board_id, view_kind)` and
 * the local key is `molt:board-view-chrome:<userId>:<boardId>` — user AND
 * board on both sides, so the server is a strict upgrade of the local record
 * rather than a differently-shaped state. The leading `user_id` is also why
 * the whole-list `PUT` is legitimate against SC-4 and why the route emits no
 * event: there is no second writer, and a broadcast would leak one member's
 * private chrome to every socket on the board.
 */

export function getBoardPinnedViews(boardId: string): Promise<BoardPinnedViews> {
  return get(
    `/boards/${encodeURIComponent(boardId)}/pinned-views`,
    boardPinnedViewsSchema,
  );
}

/**
 * ⚠ `async`, and `parse` before `put`, for the two reasons `setFocusMode`
 * gives above. `async` so a schema rejection is a REJECTED PROMISE rather
 * than a synchronous throw — a synchronous throw from a `mutationFn` escapes
 * the mutation's own error handling. `parse` rather than
 * `normalisePinnedViews` so a duplicate or a misspelt kind is REFUSED here
 * instead of being silently repaired: the caller has already cleaned its
 * array, so anything dirty arriving at this line is a bug in the caller, and
 * a repair would hide it behind a request that looks like it worked.
 *
 * ⚠ `views` is an ORDER. It goes out in the order it arrives — no sort, no
 * `Set` round-trip. The `position` lexorank that backs the order is assigned
 * server-side and never appears on the wire, which is what keeps the
 * `COLLATE "C"` trap inside the server where the web consumer cannot
 * reintroduce it.
 */
export async function setBoardPinnedViews(
  boardId: string,
  views: readonly BoardViewKind[],
): Promise<BoardPinnedViews> {
  return put(
    `/boards/${encodeURIComponent(boardId)}/pinned-views`,
    boardPinnedViewsSchema,
    boardPinnedViewsSchema.parse({ views: [...views] }),
  );
}

/**
 * `enabled` gates the query on a real session, copying
 * `displayPreferencesQueryOptions`. An anonymous board reader has no member
 * state at all, so asking would 401 on every anonymous board open — and the
 * strip that consumes this is already `signedIn`-gated on the board face.
 */
export function boardPinnedViewsQueryOptions(boardId: string, signedIn: boolean) {
  return queryOptions({
    queryKey: queryKeys.boards.pinnedViews(boardId),
    queryFn: () => getBoardPinnedViews(boardId),
    enabled: signedIn,
  });
}

/**
 * ⚠ SERIALISED, not optimistic — and this is the one deliberate departure
 * from `useSetBoardViewPreference` and `useSetFocusMode` beside it.
 *
 * Pins are reordered by DRAG, so a whole-list `PUT` fires per drop and two
 * drops overlap. The optimistic shape those two hooks use guards with "does
 * the cache still hold *my* value"; that works for a boolean and for a single
 * `lastView`, but for an ordered array it makes the LAST RESPONSE win rather
 * than the last INTENT. Worse, two overlapping whole-list replaces leave the
 * SERVER's own result decided by interleaving too, since the handler is a
 * delete plus upserts inside one transaction.
 *
 * `scope.id` makes `MutationCache` run these one at a time for a given board:
 * a second mutation with the same scope is PAUSED until the first settles, so
 * the last request sent is the last intent by construction, on both sides of
 * the wire. An idempotent re-`PUT` costs nothing —
 * `trg_board_pinned_views_set_updated_at` fires `WHEN (old.* IS DISTINCT FROM
 * new.*)`, so a redundant replace is a true no-op in the database.
 *
 * ⚠ NO `onMutate`, deliberately. A paused mutation defers its `onMutate`, so
 * an optimistic write here would land LATE and out of order. It is not needed:
 * the strip renders from `BoardFace`'s own `useState`, which
 * `setPinnedViewsForCurrentMember` updates synchronously before this mutation
 * is fired, so the drag is instant regardless of the queue. The cache is
 * written in `onSuccess` from the route's re-read — never from the echoed
 * body.
 *
 * ⚠ Recorded, not solved here: because there is no optimistic write, a FAILED
 * `PUT` has nothing to roll back. The cache stays honest while the board
 * face's local `useState` has already moved, so the strip and the server
 * disagree until a reload. Recovery policy belongs to the board face, which
 * owns that state; this hook must not invent one, because an invented
 * rollback here would fight the synchronous update that keeps the drag
 * instant.
 */
export function useSetBoardPinnedViews(
  boardId: string,
): UseMutationResult<BoardPinnedViews, Error, readonly BoardViewKind[]> {
  const queryClient = useQueryClient();
  const key = queryKeys.boards.pinnedViews(boardId);

  return useMutation<BoardPinnedViews, Error, readonly BoardViewKind[]>({
    scope: { id: `board-pinned-views:${boardId}` },
    mutationFn: (views) => setBoardPinnedViews(boardId, views),
    onSuccess: (pinned) => {
      queryClient.setQueryData<BoardPinnedViews>(key, pinned);
    },
  });
}
